import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import SEO from './components/SEO';
import { Toaster } from './components/ui/sonner';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { ScrollToTop } from './components/ScrollToTop';
import { ScrollToAnchor } from './components/ScrollToAnchor';
import { ScrollToTopHandler } from './components/ScrollToTopHandler';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Skeleton } from './components/ui/skeleton';
import './index.css';

const Offers = lazy(() => import('./components/Offers').then(module => ({ default: module.Offers })));
const Menu = lazy(() => import('./components/Menu').then(module => ({ default: module.Menu })));
const CartPage = lazy(() => import('./components/CartPage').then(module => ({ default: module.CartPage })));
const Footer = lazy(() => import('./components/Footer').then(module => ({ default: module.Footer })));
const SearchResults = lazy(() => import('./components/SearchResults').then(module => ({ default: module.SearchResults })));
const Deal2PizzaSelect = lazy(() => import('./components/Deal2PizzaSelect').then(module => ({ default: module.Deal2PizzaSelect })));
const Deal1PizzaSelect = lazy(() => import('./components/Deal1PizzaSelect').then(module => ({ default: module.Deal1PizzaSelect })));
const SonderAngebot = lazy(() => import('./components/SonderAngebot').then(module => ({ default: module.SonderAngebot })));

gsap.registerPlugin(ScrollTrigger);

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    gsap.fromTo(
      '.animate-fade-in',
      { opacity: 0, y: 20 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.1,
        scrollTrigger: {
          trigger: '.animate-fade-in',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );
  }, []);

  return (
    <BrowserRouter>
      <HelmetProvider>
        <ScrollToTopHandler />
        <ThemeProvider>
          <LanguageProvider>
            <SEO />
            <CartProvider>
              <div className="App min-h-screen bg-background text-foreground">
                <Navbar onCartClick={() => setIsCartOpen(true)} />

                <Routes>
                  <Route path="/" element={
                    <>
                      <ScrollToAnchor />
                      <Hero />
                      <Suspense fallback={
                        <div className="py-20 max-w-7xl mx-auto px-4">
                          <Skeleton className="h-12 w-48 mx-auto mb-12" />
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <Skeleton className="h-64 w-full" />
                            <Skeleton className="h-64 w-full" />
                            <Skeleton className="h-64 w-full" />
                          </div>
                        </div>
                      }>
                        <Offers />
                      </Suspense>

                      <Suspense fallback={
                        <div className="py-20 max-w-7xl mx-auto px-4">
                          <Skeleton className="h-12 w-64 mx-auto mb-12" />
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Skeleton className="h-64 w-full" />
                            <Skeleton className="h-64 w-full" />
                            <Skeleton className="h-64 w-full" />
                          </div>
                        </div>
                      }>
                        <Menu />
                      </Suspense>
                    </>
                  } />

                  <Route path="/search" element={
                    <Suspense fallback={
                      <div className="py-20 max-w-7xl mx-auto px-4">
                        <Skeleton className="h-12 w-48 mx-auto mb-8" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                          <Skeleton className="h-64 w-full" />
                          <Skeleton className="h-64 w-full" />
                          <Skeleton className="h-64 w-full" />
                        </div>
                      </div>
                    }>
                      <SearchResults />
                    </Suspense>
                  } />

                  <Route path="/cart" element={
                    <Suspense fallback={<div className="h-screen flex items-center justify-center"><Skeleton className="h-64 w-64" /></div>}>
                      <CartPage />
                    </Suspense>
                  } />

                  <Route path="/deal/2/pizzas" element={
                    <Suspense fallback={<div className="py-20 max-w-7xl mx-auto px-4"><Skeleton className="h-12 w-48 mx-auto mb-8" /></div>}>
                      <Deal2PizzaSelect />
                    </Suspense>
                  } />
                  <Route path="/deal/1/pizzas" element={
                    <Suspense fallback={<div className="py-20 max-w-7xl mx-auto px-4"><Skeleton className="h-12 w-48 mx-auto mb-8" /></div>}>
                      <Deal1PizzaSelect />
                    </Suspense>
                  } />
                  <Route path="/sonder-angebot" element={
                    <Suspense fallback={<div className="py-20 max-w-7xl mx-auto px-4"><Skeleton className="h-12 w-48 mx-auto mb-8" /></div>}>
                      <SonderAngebot />
                    </Suspense>
                  } />
                </Routes>

                <Suspense fallback={<Skeleton className="h-20 w-full" />}>
                  <Footer />
                </Suspense>

                <ScrollToTop />
                <Toaster position="bottom-left" />
              </div>
            </CartProvider>
          </LanguageProvider>
        </ThemeProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}

export default App;
